import type { MbcBrandContext } from "@nlc/shared-types";

import { MBC_MASTRA_API_URL } from "../config/mbc-chat.config";
import { updateRequestDraft } from "./request-draft-api";
import { requestIdFromSessionId } from "./session-ids";

type SaveDraftInput = {
  brand?: MbcBrandContext | null;
  brandId?: null | number;
  designType?: null | number;
  projectId?: null | number;
  resourceId: string;
  threadId: string;
  title?: null | string;
};

export async function saveDraft({
  threadId,
  ...body
}: SaveDraftInput): Promise<void> {
  const response = await fetch(
    `${MBC_MASTRA_API_URL}/mbc/drafts/${encodeURIComponent(threadId)}`,
    {
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    },
  );

  if (!response.ok) throw new Error(`HTTP ${response.status}`);
}

type SaveMbcDraftInput = {
  brandId?: null | number;
  designType?: null | number;
  projectId: number;
  resourceId?: string;
  threadId: string;
  title: null | string;
};

/**
 * Everything the "Save to drafts" action does (T9): PATCH the request's
 * identity fields onto the MySQL row T8 already created, then persist the
 * brief in Mongo as before. Never creates a request — `threadId` is
 * `mbc-request-{id}` by construction (D2), so a second save re-derives the
 * same id and PATCHes it again instead of creating a new one (MRB-04).
 */
export async function saveMbcDraft({
  brandId,
  designType,
  projectId,
  resourceId,
  threadId,
  title,
}: SaveMbcDraftInput): Promise<void> {
  const requestId = requestIdFromSessionId(threadId);

  if (requestId == null) {
    throw new Error(`Cannot resolve a request id from thread "${threadId}"`);
  }

  await updateRequestDraft(requestId, {
    brandId: brandId ?? undefined,
    designTypeId: designType ?? undefined,
    name: title ?? undefined,
  });

  await saveDraft({
    brandId,
    designType,
    projectId,
    resourceId: resourceId ?? String(projectId),
    threadId,
  });
}

export async function discardSession(threadId: string): Promise<void> {
  const response = await fetch(
    `${MBC_MASTRA_API_URL}/mbc/threads/${encodeURIComponent(threadId)}`,
    { method: "DELETE" },
  );

  if (!response.ok) throw new Error(`HTTP ${response.status}`);
}
